import type { ChainData } from '#lib/models/index.js'
import { m } from '#lib/paraglide/messages.js'
import { getToken, isChainPos, isChainPow } from '#lib/utils/chainsHelpers.js'
import { METRIC_CATEGORIES } from '#lib/utils/chart.js'
import {
	formatAvgTxFee,
	formatCommits,
	formatCount,
	formatDevelopers,
	formatHashrate,
	formatMiners,
	formatPrice,
	formatRepos,
	formatRevenue,
	formatStake,
	formatStars,
	formatTime,
	formatTps,
	formatTxns,
	formatUsd,
	formatValidators,
	formatWatchers,
} from '#lib/utils/formatters.js'
import { LINE_CHART_METRICS, chainSchema } from '#lib/utils/schema.js'

export type LineChartMetric = ReturnType<typeof chainSchema.parse>['type-tc']

export type LineChartTable =
	'chart_scalability' | 'chart_decentralization' | 'chart_dev_activity' | 'chart_financials'

type LineChartMetricConfig = {
	category: keyof typeof METRIC_CATEGORIES
	name: () => string
	// Menu label, when it differs from the name, ex. "Stake (USD)"
	menuLabel?: (chains: ChainData[]) => string
	table: LineChartTable
	column: string
	// Current value, to offer the metric only when every chain has it
	current: (chain: ChainData) => Nullable<number>
	format: (value: number, chain: ChainData) => string
	// Axis ticks, when they differ from `format`
	tickFormat?: (value: number, chain: ChainData) => string
	// Native-token metrics only make sense for a single chain
	singleChainOnly?: boolean
	// Only when every chain has this consensus
	consensus?: 'pos' | 'pow'
}

const tokenLabel = (name: string, chains: ChainData[], type: 'stake' | 'gas') =>
	`${name} (${getToken(chains[0], type) ?? 'Native'})`

export const LINE_CHART_METRIC_CONFIG: Record<LineChartMetric, LineChartMetricConfig> = {
	tps: {
		category: 'scalability',
		name: m.metric_tps_name,
		table: 'chart_scalability',
		column: 'tps',
		current: c => c.tps?.value,
		format: v => formatTps(v),
		tickFormat: v => formatTps(v, 'none', 'standard', 3),
	},
	txns: {
		category: 'scalability',
		name: m.metric_txns_name,
		table: 'chart_scalability',
		column: 'total_txns',
		current: c => c.txns,
		format: v => formatTxns(v),
		tickFormat: v => formatTxns(v, 'none', 'compact'),
	},
	blocktime: {
		category: 'scalability',
		name: m.metric_blocktime_name,
		table: 'chart_scalability',
		column: 'block_time',
		current: c => c.blocktime,
		format: v => formatTime(v),
	},
	nc: {
		category: 'decentralization',
		name: m.metric_nc_name,
		table: 'chart_decentralization',
		column: 'nc',
		current: c => c.nc?.value,
		format: v => formatCount(v),
	},
	stake: {
		category: 'decentralization',
		name: m.metric_stake_name,
		menuLabel: () => `${m.metric_stake_name()} (USD)`,
		table: 'chart_decentralization',
		column: 'stake_usd',
		current: c => c.stakeUsd?.value,
		format: (v, chain) => formatStake(v, chain, 'usd'),
		tickFormat: v => formatUsd(v),
		consensus: 'pos',
	},
	stake_native: {
		category: 'decentralization',
		name: m.metric_stake_native_name,
		menuLabel: chains => tokenLabel(m.metric_stake_name(), chains, 'stake'),
		table: 'chart_decentralization',
		column: 'stake_hashrate',
		current: c => c.stakeOrHashrate?.value,
		format: (v, chain) => formatStake(v, chain, 'native'),
		tickFormat: v => formatTxns(v, 'none', 'compact'),
		singleChainOnly: true,
		consensus: 'pos',
	},
	validators: {
		category: 'decentralization',
		name: m.metric_validators_name,
		table: 'chart_decentralization',
		column: 'validators_miners',
		current: c => c.validatorsOrMiners?.value,
		format: v => formatValidators(v),
		consensus: 'pos',
	},
	hashrate: {
		category: 'decentralization',
		name: m.metric_hashrate_name,
		table: 'chart_decentralization',
		column: 'stake_hashrate',
		current: c => c.stakeOrHashrate?.value,
		format: formatHashrate,
		consensus: 'pow',
	},
	miners: {
		category: 'decentralization',
		name: m.metric_miners_name,
		table: 'chart_decentralization',
		column: 'validators_miners',
		current: c => c.validatorsOrMiners?.value,
		format: v => formatMiners(v),
		consensus: 'pow',
	},
	developers: {
		category: 'dev-activity',
		name: m.metric_developers_name,
		table: 'chart_dev_activity',
		column: 'devs',
		current: c => c.developers?.value,
		format: v => formatDevelopers(v),
	},
	commits: {
		category: 'dev-activity',
		name: m.metric_commits_name,
		table: 'chart_dev_activity',
		column: 'commits',
		current: c => c.commits?.value,
		format: v => formatCommits(v),
	},
	repos: {
		category: 'dev-activity',
		name: m.metric_repos_name,
		table: 'chart_dev_activity',
		column: 'repos',
		current: c => c.repos?.value,
		format: v => formatRepos(v),
	},
	stars: {
		category: 'dev-activity',
		name: m.metric_stars_name,
		table: 'chart_dev_activity',
		column: 'stars',
		current: c => c.stars?.value,
		format: v => formatStars(v),
	},
	watchers: {
		category: 'dev-activity',
		name: m.metric_watchers_name,
		table: 'chart_dev_activity',
		column: 'watchers',
		current: c => c.watchers?.value,
		format: v => formatWatchers(v),
	},
	revenue: {
		category: 'financials',
		name: m.metric_revenue_name,
		menuLabel: () => `${m.metric_revenue_name()} (USD)`,
		table: 'chart_financials',
		column: 'revenue_usd',
		current: c => c.revenueUsd?.value,
		format: v => formatRevenue(v),
	},
	revenue_native: {
		category: 'financials',
		name: m.metric_revenue_native_name,
		menuLabel: chains => tokenLabel(m.metric_revenue_name(), chains, 'gas'),
		table: 'chart_financials',
		column: 'revenue',
		current: c => c.revenue?.value,
		format: (v, chain) => formatRevenue(v, chain, 'native'),
		singleChainOnly: true,
	},
	avg_tx_fee: {
		category: 'financials',
		name: m.metric_avg_tx_fee_name,
		menuLabel: () => `${m.metric_avg_tx_fee_name_short()} (USD)`,
		table: 'chart_financials',
		column: 'tx_fee_usd',
		current: c => c.avgTxFeeUsd?.value,
		format: v => formatAvgTxFee(v),
	},
	avg_tx_fee_native: {
		category: 'financials',
		name: m.metric_avg_tx_fee_native_name,
		menuLabel: chains => tokenLabel(m.metric_avg_tx_fee_name_short(), chains, 'gas'),
		table: 'chart_financials',
		column: 'tx_fee',
		current: c => c.avgTxFee?.value,
		format: (v, chain) => formatAvgTxFee(v, chain, 'native'),
		singleChainOnly: true,
	},
	price: {
		category: 'financials',
		name: m.metric_price_name,
		table: 'chart_financials',
		column: 'price',
		current: c => c.price?.value,
		format: formatPrice,
	},
	market_cap: {
		category: 'financials',
		name: m.metric_market_cap_name,
		table: 'chart_financials',
		column: 'market_cap',
		current: c => c.marketCap?.value,
		format: formatUsd,
	},
}

const isAvailable = (metric: LineChartMetric, chains: ChainData[]) => {
	const { singleChainOnly, consensus, current } = LINE_CHART_METRIC_CONFIG[metric]

	if (singleChainOnly && chains.length !== 1) return false
	if (consensus && !chains.every(consensus === 'pos' ? isChainPos : isChainPow)) return false

	return chains.every(chain => current(chain) != null)
}

// Metric menu for these chains; categories without metrics are left out
export const getLineChartMetricMenu = (chains: ChainData[]) =>
	Object.entries(METRIC_CATEGORIES)
		.map(([categoryName, label]) => ({
			label: label(),
			categoryName,
			items: LINE_CHART_METRICS.filter(
				metric =>
					LINE_CHART_METRIC_CONFIG[metric].category === categoryName && isAvailable(metric, chains)
			).map(metric => {
				const { name, menuLabel } = LINE_CHART_METRIC_CONFIG[metric]

				return { label: menuLabel ? menuLabel(chains) : name(), value: metric }
			}),
		}))
		.filter(category => category.items.length > 0)
